import React, { useState, useEffect } from "react";
import "./PaymentModal.scss";

//slice actions to make payment
import { createTenantPayment,getProfile } from "../../../../redux/slices/sliceActions";

//imports for user data
import { useSelector, useDispatch } from "react-redux";


const PaymentModal = ({
  isOpen,
  closeModal,
  defaultValues = {},
  fieldsToShow = [],
}) => {
  //user data
  const dispatch = useDispatch();
  const { user, profile } = useSelector((state) => state.landlordslice);
  const token = user?.accessToken;
  const id = user?.user?.user_id;

  const [paymentDetails, setPaymentDetails] = useState({
    rent: defaultValues.rent || "",
    water: defaultValues.water || "",
    arrears: defaultValues.arrears || "",
    serviceCharge: defaultValues.serviceCharge || "",
    unpaidDeposit: defaultValues.unpaidDeposit || "",
    garbage: defaultValues.garbage || "",
    paymentMethod: defaultValues.paymentMethod || "M-Pesa",
    phoneNumber: defaultValues.phoneNumber || "",
    referenceNumber: defaultValues.referenceNumber || "",
    paymentNarration: defaultValues.paymentNarration || "",
  });

  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    setPaymentDetails({
      rent: defaultValues.rent || "",
      water: defaultValues.water || "",
      arrears: defaultValues.arrears || "",
      serviceCharge: defaultValues.serviceCharge || "",
      unpaidDeposit: defaultValues.unpaidDeposit || "",
      garbage: defaultValues.garbage || "",
      paymentMethod: defaultValues.paymentMethod || "M-Pesa",
      phoneNumber: defaultValues.phoneNumber || "",
      referenceNumber: defaultValues.referenceNumber || "",
      paymentNarration: defaultValues.paymentNarration || "",
    });
    setLoading(false);
    setComplete(false);
  }, [defaultValues]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "number" ? (value ? parseInt(value, 10) : "") : value;
    setPaymentDetails({ ...paymentDetails, [name]: val });
  };

  //submit payment
  const [submissionState, setSubmissionState] = useState("idle"); // 'idle', 'loading', 'complete'
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionState("loading");

    const newPaymentDetails = {
      houseId: Number(user?.house?.house_id),
      houseNumber: user?.house?.house_number,
      paymentMethod: paymentDetails.paymentMethod || "CASH",
      paymentReference: paymentDetails.paymentReference || "",
      paymentNarration: paymentDetails.paymentNarration || "",
      payerTransactionReference: paymentDetails.referenceNumber || "",
      paymentDate: new Date().toISOString().split("T")[0],
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      rentAmount: paymentDetails.rent || 0,
      waterAmount: paymentDetails.water || 0,
      garbageAmount: paymentDetails.garbage || 0,
      serviceFeeAmount: paymentDetails.serviceCharge || 0,
      lateFeeAmount: paymentDetails.lateFee || 0,
      arrearsAmount: paymentDetails.arrears || 0,
      unpaidDepositAmount: paymentDetails.unpaidDeposit || 0,
    };

    try {
      await dispatch(
        createTenantPayment({
          propertyId: profile?.tenant_details?.house_info?.property_id,
          details: newPaymentDetails,
          token,
        })
      );
      await dispatch(getProfile({ id, token }));
      setSubmissionState("complete");

      setTimeout(() => {
        closeModal();
        setSubmissionState("idle");
      }, 2000); // Adjust timing as needed
    } catch (error) {
      console.error("Payment submission failed", error);
      setSubmissionState("idle");
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pay-window-title">Make a Payment</div>

        {submissionState === "loading" ? (
          <div className="loading-container">
            <div className="loader"></div>
          </div>
        ) : submissionState === "complete" ? (
          <div className="complete-container">
            <div className="complete-animation">Complete!</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Payment Method Selection */}
            <div className="form-group">
              <label htmlFor="paymentMethod">Payment Method:</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={paymentDetails.paymentMethod}
                onChange={handleChange}
              >
                <option value="PAYBILL">M-Pesa</option>
                <option value="Lipa na M-Pesa STK">Lipa na M-Pesa STK</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CASH">Cash</option>
              </select>
            </div>

            {/* Conditionally render fields based on payment method */}
            {paymentDetails.paymentMethod === "Lipa na M-Pesa STK" && (
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number:</label>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={paymentDetails.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {["M-Pesa", "Bank Transfer"].includes(
              paymentDetails.paymentMethod
            ) && (
              <div className="form-group">
                <label htmlFor="referenceNumber">Reference Number:</label>
                <input
                  type="text"
                  id="referenceNumber"
                  name="referenceNumber"
                  value={paymentDetails.referenceNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {/* Payment Narration Field */}
            <div className="form-group">
              <label htmlFor="paymentNarration">Payment Narration:</label>
              <input
                type="text"
                id="paymentNarration"
                name="paymentNarration"
                value={paymentDetails.paymentNarration}
                onChange={handleChange}
              />
            </div>

            {/* Dynamically render fields based on fieldsToShow prop */}
            {fieldsToShow.includes("rent") && (
              <div className="form-group">
                <label htmlFor="rent">Rent:</label>
                <input
                  type="number"
                  id="rent"
                  name="rent"
                  value={paymentDetails.rent}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {fieldsToShow.includes("water") && (
              <div className="form-group">
                <label htmlFor="water">Water:</label>
                <input
                  type="number"
                  id="water"
                  name="water"
                  value={paymentDetails.water}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {fieldsToShow.includes("arrears") && (
              <div className="form-group">
                <label htmlFor="arrears">Arrears:</label>
                <input
                  type="number"
                  id="arrears"
                  name="arrears"
                  value={paymentDetails.arrears}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {fieldsToShow.includes("serviceCharge") && (
              <div className="form-group">
                <label htmlFor="serviceCharge">Service Charge:</label>
                <input
                  type="number"
                  id="serviceCharge"
                  name="serviceCharge"
                  value={paymentDetails.serviceCharge}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {fieldsToShow.includes("unpaidDeposit") && (
              <div className="form-group">
                <label htmlFor="unpaidDeposit">Unpaid Deposit:</label>
                <input
                  type="number"
                  id="unpaidDeposit"
                  name="unpaidDeposit"
                  value={paymentDetails.unpaidDeposit}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {fieldsToShow.includes("garbage") && (
              <div className="form-group">
                <label htmlFor="garbage">Garbage:</label>
                <input
                  type="number"
                  id="garbage"
                  name="garbage"
                  value={paymentDetails.garbage}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            <button type="submit" className="submit-btn">
              Submit Payment
            </button>
            <button type="button" className="close-btn" onClick={closeModal}>
              Close
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
